import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ProofImageGallery from "@/components/ProofImageGallery";
import { InlineHint, PageEmptyState, PageLoadingState } from "@/components/PageStates";
import { PageHero, PageShell } from "@/components/PageShell";
import { toDateInputValue } from "@/lib/date";
import { buildProofImageSrc, getTransactionProofImageUrls } from "@/lib/proofImages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sanitizeMonth = (value) => {
  const normalized = Number.parseInt(value, 10);
  if (Number.isNaN(normalized)) {
    return new Date().getMonth() + 1;
  }
  return Math.min(Math.max(normalized, 1), 12);
};

const sanitizeYear = (value) => {
  const normalized = Number.parseInt(value, 10);
  if (Number.isNaN(normalized)) {
    return new Date().getFullYear();
  }
  return normalized;
};

const buildBulananDateRange = (bulan, tahun) => {
  const startDate = new Date(tahun, bulan - 1, 1);
  const endDate = new Date(tahun, bulan, 0);
  return {
    tanggalMulai: toDateInputValue(startDate),
    tanggalAkhir: toDateInputValue(endDate),
  };
};

const SummaryCard = ({ label, value, tone = "amber" }) => {
  const toneClasses = {
    amber: "border-amber-200 bg-gradient-to-br from-amber-50 to-white text-amber-900",
    sky: "border-sky-200 bg-gradient-to-br from-sky-50 to-white text-sky-900",
  };

  return (
    <div className={`rounded-2xl border p-6 shadow-sm ${toneClasses[tone] || toneClasses.amber}`}>
      <p className="mb-2 text-sm font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="text-2xl font-manrope font-bold">{value}</p>
    </div>
  );
};

const LaporanBarangKeluarPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryBulan = sanitizeMonth(searchParams.get("bulan"));
  const queryTahun = sanitizeYear(searchParams.get("tahun"));

  const [bulan, setBulan] = useState(queryBulan);
  const [tahun, setTahun] = useState(queryTahun);
  const [barangs, setBarangs] = useState([]);
  const [laporanBarangKeluar, setLaporanBarangKeluar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTransaksiDetail, setSelectedTransaksiDetail] = useState(null);

  useEffect(() => {
    setBulan(queryBulan);
    setTahun(queryTahun);
  }, [queryBulan, queryTahun]);

  useEffect(() => {
    const fetchBarang = async () => {
      try {
        const response = await axios.get(`${API}/barang`);
        setBarangs(response.data.filter((barang) => barang.aktif !== false));
      } catch {
        setBarangs([]);
      }
    };

    fetchBarang();
  }, []);

  const getBarangName = (barangId) => {
    const barang = barangs.find((item) => item.barang_id === barangId);
    return barang ? barang.nama_barang : "-";
  };

  const getBarangSatuan = (barangId) => {
    const barang = barangs.find((item) => item.barang_id === barangId);
    return barang ? barang.satuan : "";
  };

  const getItemQtyWithSatuan = (item) => {
    const qtyDisplay = item?.qty_input ?? item?.qty ?? 0;
    const satuanDisplay = item?.satuan_input || getBarangSatuan(item?.barang_id) || "-";
    return `${qtyDisplay} ${satuanDisplay}`;
  };

  const formatDateTimeId = (tanggalValue) => {
    if (!tanggalValue) {
      return "-";
    }

    const tanggalObj = new Date(tanggalValue);
    if (Number.isNaN(tanggalObj.getTime())) {
      return "-";
    }

    return tanggalObj.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const selectedTransaksiFotoGalleryItems = getTransactionProofImageUrls(selectedTransaksiDetail).map((url, index) => {
    const imageSrc = buildProofImageSrc(url);
    return {
      id: `${selectedTransaksiDetail?.keluar_id || "laporan-keluar"}-${index}`,
      src: imageSrc,
      href: imageSrc,
      alt: `Foto bukti transaksi ${index + 1}`,
      label: `Foto ${index + 1}`,
    };
  });

  const formatDateId = (tanggalValue) => {
    if (!tanggalValue) {
      return "-";
    }

    const tanggalObj = new Date(tanggalValue);
    if (Number.isNaN(tanggalObj.getTime())) {
      return "-";
    }

    return tanggalObj.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatTimeId = (tanggalValue) => {
    if (!tanggalValue) {
      return "-";
    }

    const tanggalObj = new Date(tanggalValue);
    if (Number.isNaN(tanggalObj.getTime())) {
      return "-";
    }

    return tanggalObj.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchBarangKeluarBulanan = async (
    targetBulan = bulan,
    targetTahun = tahun,
    updateUrl = true
  ) => {
    const normalizedBulan = sanitizeMonth(targetBulan);
    const normalizedTahun = sanitizeYear(targetTahun);
    const targetParams = {
      bulan: String(normalizedBulan),
      tahun: String(normalizedTahun),
    };

    if (
      updateUrl &&
      (searchParams.get("bulan") !== targetParams.bulan ||
        searchParams.get("tahun") !== targetParams.tahun)
    ) {
      setSearchParams(targetParams);
      return;
    }

    const { tanggalMulai, tanggalAkhir } = buildBulananDateRange(normalizedBulan, normalizedTahun);

    setLoading(true);
    try {
      const response = await axios.get(`${API}/laporan/bulanan/preview`, {
        params: {
          tanggal_mulai: tanggalMulai,
          tanggal_akhir: tanggalAkhir,
          mode: "harian_multi_supplier",
        },
      });
      setLaporanBarangKeluar(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memuat laporan barang keluar bulanan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBarangKeluarBulanan(queryBulan, queryTahun, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryBulan, queryTahun]);

  const handleOpenTransaksiDetail = (transaksiItem) => {
    setSelectedTransaksiDetail(transaksiItem);
    setDetailDialogOpen(true);
  };

  const handleDetailDialogChange = (open) => {
    setDetailDialogOpen(open);
    if (!open) {
      setSelectedTransaksiDetail(null);
    }
  };

  return (
    <PageShell data-testid="laporan-barang-keluar-page">
      <PageHero
        tone="amber"
        eyebrow="Monitoring Laporan"
        title="Laporan Barang Keluar"
        description="Halaman ini menampilkan transaksi barang keluar bulanan secara terpisah agar lebih fokus dibaca dan ditinjau."
        actions={(
          <Button type="button" variant="outline" onClick={() => navigate("/laporan")}>
            <ArrowLeft size={16} className="mr-2" />
            Kembali ke Laporan
          </Button>
        )}
      />

      <div className="rounded-2xl border border-amber-200 bg-white/95 p-6 shadow-sm">
        <h2 className="mb-4 font-manrope text-xl font-bold">Filter Barang Keluar Bulanan</h2>
        <InlineHint className="mb-4 border-amber-200/80 bg-amber-50/70">
          Transaksi barang keluar ditampilkan per tanggal. Klik salah satu transaksi untuk melihat detail item dan keterangan pengeluaran.
        </InlineHint>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px] lg:items-end">
          <div className="space-y-2">
            <Label htmlFor="laporan-keluar-bulan">Bulan</Label>
            <Input
              id="laporan-keluar-bulan"
              type="number"
              min="1"
              max="12"
              value={bulan}
              onChange={(event) => setBulan(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="laporan-keluar-tahun">Tahun</Label>
            <Input
              id="laporan-keluar-tahun"
              type="number"
              value={tahun}
              onChange={(event) => setTahun(event.target.value)}
            />
          </div>
          <Button type="button" onClick={() => fetchBarangKeluarBulanan()} disabled={loading}>
            <FileText size={16} className="mr-2" />
            Tampilkan Transaksi
          </Button>
        </div>
      </div>

      {loading ? (
        <PageLoadingState
          label="Memuat transaksi barang keluar..."
          description="Mohon tunggu, daftar transaksi bulanan sedang disiapkan."
        />
      ) : laporanBarangKeluar ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SummaryCard
              label="Transaksi Barang Keluar"
              value={`${laporanBarangKeluar.summary.jumlah_transaksi_keluar} transaksi`}
            />
            <SummaryCard
              label="Total Qty Dasar"
              value={Number(laporanBarangKeluar.summary.total_barang_keluar || 0).toLocaleString("id-ID")}
              tone="sky"
            />
          </div>

          {laporanBarangKeluar.barang_keluar_sections.length === 0 ? (
            <PageEmptyState
              title="Tidak ada transaksi barang keluar"
              description="Belum ada transaksi barang keluar pada periode bulanan yang dipilih."
            />
          ) : (
            <div className="rounded-lg border border-orange-200 bg-orange-50/70 p-6 shadow-sm space-y-5">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="font-manrope font-bold text-xl">Daftar Barang Keluar</h3>
                  <p className="text-sm text-muted-foreground">
                    Periode {formatDateId(laporanBarangKeluar.periode.tanggal_mulai)} - {formatDateId(laporanBarangKeluar.periode.tanggal_akhir)}
                  </p>
                </div>
                <span className="text-sm font-medium text-orange-800">
                  {laporanBarangKeluar.summary.jumlah_transaksi_keluar} transaksi
                </span>
              </div>

              <div className="space-y-4">
                {laporanBarangKeluar.barang_keluar_sections.map((section) => (
                  <div key={`keluar-${section.tanggal}`} className="rounded-xl border border-orange-200/80 bg-white/90 p-4 shadow-sm space-y-4">
                    <div className="flex flex-col gap-2 border-b border-orange-100 pb-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="font-manrope font-bold text-lg text-slate-900">
                          {section.tanggal_label || formatDateId(section.tanggal)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {section.jumlah_transaksi} transaksi barang keluar
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-orange-800">
                        Total qty dasar {Number(section.total_qty || 0).toLocaleString("id-ID")}
                      </p>
                    </div>

                    <div className="space-y-3">
                      {section.transaksi.map((transaksiItem, idx) => (
                        <button
                          key={transaksiItem.keluar_id || `barang-keluar-${section.tanggal}-${idx}`}
                          type="button"
                          onClick={() => handleOpenTransaksiDetail(transaksiItem)}
                          className="w-full rounded-md border border-orange-200 bg-orange-50/60 p-4 text-left transition-colors hover:bg-orange-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="mb-1 font-medium">
                                {transaksiItem.items?.length || 0} item keluar
                              </p>
                              <div className="text-sm text-muted-foreground">
                                Petugas: {transaksiItem.petugas || "-"}
                                {transaksiItem.keterangan ? ` - ${transaksiItem.keterangan}` : ""}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Jam: {formatTimeId(transaksiItem.tanggal)}
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                {formatDateTimeId(transaksiItem.tanggal)}
                              </div>
                            </div>
                            <span className="text-xs font-semibold text-orange-700">Lihat Detail</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      <Dialog open={detailDialogOpen} onOpenChange={handleDetailDialogChange}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Barang Keluar</DialogTitle>
          </DialogHeader>

          {selectedTransaksiDetail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tanggal</p>
                  <p className="font-medium">{formatDateTimeId(selectedTransaksiDetail.tanggal)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Petugas</p>
                  <p className="font-medium">{selectedTransaksiDetail.petugas || "-"}</p>
                </div>
                {selectedTransaksiDetail.keterangan ? (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Keterangan</p>
                    <p className="font-medium">{selectedTransaksiDetail.keterangan}</p>
                  </div>
                ) : null}
              </div>

              {selectedTransaksiFotoGalleryItems.length > 0 ? (
                <ProofImageGallery
                  title={`Foto Bukti (${selectedTransaksiFotoGalleryItems.length})`}
                  hint="Klik gambar untuk membuka ukuran penuh. Semua bukti ditampilkan dengan ukuran thumbnail yang konsisten."
                  images={selectedTransaksiFotoGalleryItems}
                />
              ) : null}

              <div>
                <p className="mb-2 text-sm font-manrope font-semibold">
                  Daftar Item ({(selectedTransaksiDetail.items || []).length})
                </p>
                <div className="overflow-hidden rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="w-14 px-3 py-2 text-center">No</th>
                        <th className="px-3 py-2 text-left">Barang</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-left">Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedTransaksiDetail.items || []).map((item, idx) => (
                        <tr key={`${selectedTransaksiDetail.keluar_id || "detail-keluar"}-${idx}`} className="border-t">
                          <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                          <td className="px-3 py-2">{getBarangName(item.barang_id)}</td>
                          <td className="px-3 py-2 text-center">{getItemQtyWithSatuan(item)}</td>
                          <td className="px-3 py-2">{item.keterangan || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default LaporanBarangKeluarPage;
